"use client";

import { useEffect, useState } from "react";
import { type RouterOutput, trpc } from "@/lib/trpc-client";
import { useCreatorAuth } from "../creatorAuthContext";

export type BrandDirectoryEntry =
  RouterOutput["creatorPortal"]["getBrandDirectory"]["brands"][number];

/** What a brand's row is waiting on, if anything. */
export type BrandBusy = "applying" | "withdrawing" | null;

/**
 * The brand list and the two things a creator does to it.
 *
 * After either mutation the list and the account are both read again: the
 * list for the brand's new state and the waiting count, the account because
 * a brand application is part of what decides whether someone gets in at all.
 * The mutation stays pending until both reads land, so a row never flicks
 * back to Apply between the server saying yes and the list saying so.
 */
export function useBrandDirectory() {
  const utils = trpc.useUtils();
  const { account } = useCreatorAuth();
  const directory = trpc.creatorPortal.getBrandDirectory.useQuery(undefined, {
    retry: false,
  });
  // One failure at a time, on the row it belongs to. The server words these
  // for the creator (already applied, three waiting, already decided), so its
  // message is what is shown.
  const [failure, setFailure] = useState<{
    orgSlug: string;
    message: string;
  } | null>(null);

  const refresh = () =>
    Promise.all([
      utils.creatorPortal.getBrandDirectory.invalidate(),
      utils.creatorAuth.me.invalidate(),
    ]);
  const applyMutation = trpc.creatorPortal.applyToBrand.useMutation({
    onSettled: refresh,
  });
  const withdrawMutation =
    trpc.creatorPortal.withdrawBrandApplication.useMutation({
      onSettled: refresh,
    });

  const apply = (brand: BrandDirectoryEntry) => {
    setFailure(null);
    applyMutation.mutate(
      { orgSlug: brand.orgSlug },
      {
        onError: (error) =>
          setFailure({ orgSlug: brand.orgSlug, message: error.message }),
      },
    );
  };

  const withdraw = (brand: BrandDirectoryEntry) => {
    const brandApplicationId = brand.brandApplicationId;
    if (!brandApplicationId) return;
    setFailure(null);
    withdrawMutation.mutate(
      { brandApplicationId },
      {
        onError: (error) =>
          setFailure({ orgSlug: brand.orgSlug, message: error.message }),
      },
    );
  };

  const busy = (brand: BrandDirectoryEntry): BrandBusy => {
    if (
      applyMutation.isPending &&
      applyMutation.variables?.orgSlug === brand.orgSlug
    )
      return "applying";
    if (
      withdrawMutation.isPending &&
      !!brand.brandApplicationId &&
      withdrawMutation.variables?.brandApplicationId ===
        brand.brandApplicationId
    )
      return "withdrawing";
    return null;
  };

  // A brand the list says they joined that the account does not list yet:
  // the approval landed between the two reads. Read the account again so the
  // program opens now rather than on the gate's next poll. Keyed on the
  // slugs, so an account that still disagrees after the read is not asked
  // again in a loop.
  const unlisted = (directory.data?.brands ?? [])
    .filter(
      (brand) =>
        brand.status === "joined" &&
        !account?.programs.some((program) => program.slug === brand.orgSlug),
    )
    .map((brand) => brand.orgSlug)
    .join(",");
  useEffect(() => {
    if (unlisted) void utils.creatorAuth.me.invalidate();
  }, [unlisted]);

  return {
    directory,
    apply,
    withdraw,
    busy,
    // One application at a time: the server counts the waiting ones under a
    // lock, and a second tap racing the first would only come back refused.
    applying: applyMutation.isPending,
    failureFor: (brand: BrandDirectoryEntry) =>
      failure?.orgSlug === brand.orgSlug ? failure.message : null,
  };
}
